/* eslint-disable max-lines */
'use client';

import React, { useEffect, useRef, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import type { PageType } from '@/features/Analytics';
import { updateUserIdAfterBooking } from '@/features/Analytics/ui/GTMInitializer';
import { useRouter } from '@/navigation';
import { useHttp } from '@/shared/api/provider';
import type { LanguageCodeEnum } from '@/shared/config/common/constants/lang';
import { SESSION_LAST_BOOKING_TIMESTAMP } from '@/shared/config/common/constants/storage';
import { featureBookingModalConfig } from '@/shared/config/tests/e2e/features/booking';
import { useDialogFocus } from '@/shared/hooks/dom/useDialogFocus';
import { useToast } from '@/shared/hooks/ui/use-toast';
import { TrackEvent } from '@/shared/lib/analytics/component';
import { classNames } from '@/shared/lib/classnames/classnames';
import { Button } from '@/shared/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/shared/ui/dialog';
import { useCurrentBooking } from '../../model/hooks/useCurrentBooking';
import { useGABooking } from '../../model/hooks/useGABooking';
import { useMakeBooking } from '../../model/hooks/useMakeBooking';
import { useOTP } from '../../model/hooks/useOTP';
import { useOTPModal } from '../../model/hooks/useOTPModal';
import { useOTPTimer } from '../../model/hooks/useOTPTimer';
import { useBooking } from '../../model/store/booking';
import { useBookingModals } from '../../model/store/bookingModals';
import { BookingModalTitle } from './BookingModalTitle';
import { BookingOnlineForm } from './BookingOnlineForm';
import { BookingOTPForm } from './BookingOTPForm';
import { BookingTimeslots } from './BookingTimeslots';
import btnIcons from '@/shared/assets/sprites/btns.sprite.svg';
import type { IMakeBooking, IBookingDoctorData, ISchedule } from '@/entities/booking/model/types';
interface Props {
  doctorId: number;
  doctorName: string;
  medcenterId: number | null;
  medcenterName: string;
  bookingData: IBookingDoctorData[];
  pageType: PageType;
}
export const BookingOnline: React.FC<Props> = props => {
  const {
    doctorId,
    doctorName,
    medcenterId,
    medcenterName,
    bookingData,
    pageType
  } = props;
  const eventSentRef = useRef(false);
  const {
    toast
  } = useToast();
  const repositories = useHttp();
  const t = useTranslations();
  const locale = useLocale();
  const router = useRouter();
  const {
    setVisibleBookingModal,
    visibleBookingModal,
    modalBookingId
  } = useBookingModals();
  const {
    currentBooking
  } = useCurrentBooking({
    doctorId,
    medcenterId,
    bookingData
  });
  const {
    openOTPModal,
    timerHideModalsHandler,
    hideOTPModal,
    showOTPModal
  } = useOTPModal();
  const {
    startTimerCode,
    timerNewCode
  } = useOTPTimer({
    openOTPModal
  });
  const {
    resetData: resetBookingData
  } = useBooking();
  const {
    makeBookingData,
    isValidBookingData,
    submitLoading,
    setInitialMakeBooking,
    setFieldMakeBooking,
    submitBooking,
    submitNewCode
  } = useMakeBooking({
    doctorId,
    medcenterId
  });
  const {
    changeOTP,
    otp,
    statusOTP,
    statusOTPMessage,
    submitOTPLoading
  } = useOTP({
    repositories,
    phoneNumber: makeBookingData.phoneNumber
  });
  const {
    trackErrorBookingModal,
    trackOTPEvents,
    trackOpenBookingModal
  } = useGABooking({
    doctorName,
    medcenterName,
    makeBookingData,
    pageType,
    doctorId,
    referral: 'idoctor.kz'
  });
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [currentSchedule, setCurrentSchedule] = useState<ISchedule[] | null>(null);
  const {
    isInputFocused
  } = useDialogFocus({
    isOpen: visibleBookingModal,
    modalId: modalBookingId!,
    currentId: doctorId
  });
  const hideModal = () => {
    setVisibleBookingModal(false);
  };
  const goBookingPage = (code: string | null) => {
    if (code) {
      let retryCount = 0;
      const maxRetries = 3;
      const retryDelay = 500;
      resetBookingData();
      const checkBooking = () => {
        repositories.booking.checkExistBooking(code).then(data => {
          if (data?.ok) {
            sessionStorage.setItem(SESSION_LAST_BOOKING_TIMESTAMP, Date.now().toString());
            router.push(`/booking/${code}?show-back=1`);
          } else {
            retryCount++;
            if (retryCount < maxRetries) {
              setTimeout(checkBooking, retryDelay);
            } else {
              toast({
                variant: 'error',
                title: t('bookingPage.booking_check_failed')
              });
            }
          }
        }).catch(() => {
          retryCount++;
          if (retryCount < maxRetries) {
            setTimeout(checkBooking, retryDelay);
          } else {
            toast({
              variant: 'error',
              title: t('bookingPage.network_error')
            });
          }
        });
      };
      checkBooking();
    }
  };
  const submitNewCodeHandler = async () => {
    trackOTPEvents('otp_retry_simple_booking');
    submitNewCode((response: IMakeBooking) => {
      if (response.code === 1) {
        startTimerCode();
        return;
      }
      hideModal();
      if (response.isError) {
        trackOTPEvents('otp_error_simple_booking');
      }
      toast({
        variant: response.isError ? 'error' : 'success',
        title: response.message || ''
      });
    });
  };
  const submitBookingHandler = async () => {
    if (isValidBookingData) {
      submitBooking(async (response: IMakeBooking) => {
        if (response.code === 1) {
          trackOTPEvents('submit_simple_booking');
          showOTPModal();
          return;
        }
        hideModal();
        await setInitialMakeBooking();
        if (response.isError) {
          trackErrorBookingModal(response.message || '');
        } else {
          updateUserIdAfterBooking(makeBookingData.phoneNumber);
          goBookingPage(response.data.bookingCode);
          setShowSuccessToast(true);
        }
        toast({
          variant: response.isError ? 'error' : 'success',
          title: response.message || ''
        });
      });
    }
  };
  const successOTPHandler = async (response: IMakeBooking) => {
    trackOTPEvents('otp_simple_booking');
    updateUserIdAfterBooking(makeBookingData.phoneNumber);
    await setInitialMakeBooking();
    timerHideModalsHandler();
    resetBookingData();
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(SESSION_LAST_BOOKING_TIMESTAMP, Date.now().toString());
    }
    goBookingPage(response.data.bookingCode);
    setShowSuccessToast(true);
  };
  const handleOverlayClick = () => {
    if (!isSelectOpen) {
      hideModal();
    }
  };
  useEffect(() => {
    if (visibleBookingModal && modalBookingId === doctorId) {
      if (!eventSentRef.current) {
        trackOpenBookingModal();
        eventSentRef.current = true;
      }
    } else {
      eventSentRef.current = false;
    }
    if (typeof window !== 'undefined') {
      const timestamp = sessionStorage.getItem(SESSION_LAST_BOOKING_TIMESTAMP);
      if (timestamp && visibleBookingModal) {
        hideModal();
      }
    }
  }, [doctorId, modalBookingId, visibleBookingModal, trackOpenBookingModal, hideModal]);
  useEffect(() => {
    if (typeof window !== 'undefined' && document) {
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          const timestamp = sessionStorage.getItem(SESSION_LAST_BOOKING_TIMESTAMP);
          if (timestamp && visibleBookingModal) {
            hideModal();
            hideOTPModal();
          }
        }
      };
      document.addEventListener('visibilitychange', handleVisibilityChange);
      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }
  }, [visibleBookingModal, hideModal, hideOTPModal]);
  useEffect(() => {
    if (medcenterId) {
      const booking = bookingData.find(item => item.medcenterId === medcenterId);
      if (booking) {
        setCurrentSchedule(booking.onlineBooking.schedule);
      }
    }
  }, [medcenterId]);
  return <>
      <Dialog open={visibleBookingModal && modalBookingId === doctorId} data-sentry-element="Dialog" data-sentry-source-file="index.tsx">
        <DialogContent data-testid={featureBookingModalConfig.ids.bookingModal} removeScrollLock overlayClick={handleOverlayClick} className={classNames('h-dvh min-h-dvh overflow-y-scroll md:h-auto md:min-h-[auto] md:w-full md:max-w-lg md:overflow-y-hidden md:rounded-3xl', {
        'pb-72 md:pb-4': isInputFocused,
        'pb-4': !isInputFocused
      })} data-sentry-element="DialogContent" data-sentry-source-file="index.tsx">
          <div className={classNames('overflow-x-hidden', {
          hidden: openOTPModal
        })}>
            <BookingModalTitle hideModal={hideModal} doctorName={doctorName} data-sentry-element="BookingModalTitle" data-sentry-source-file="index.tsx" />

            {currentBooking && currentSchedule && <div className="mb-4" data-testid={featureBookingModalConfig.ids.bookingTimeslots}>
                <BookingTimeslots pageType={pageType} doctorId={doctorId} schedule={currentSchedule} currentBooking={currentBooking} />
              </div>}
            <div>
              {currentBooking && <BookingOnlineForm makeBookingData={makeBookingData} currentBooking={currentBooking} locale={locale as LanguageCodeEnum} setFieldMakeBooking={setFieldMakeBooking} setIsSelectOpen={setIsSelectOpen} />}
            </div>
            <DialogFooter data-sentry-element="DialogFooter" data-sentry-source-file="index.tsx">
              <Button data-testid={featureBookingModalConfig.ids.bookingSubmitButton} size="xl" className="mt-4 w-full" disabled={!isValidBookingData} onClick={submitBookingHandler} loading={submitLoading || submitOTPLoading} data-sentry-element="Button" data-sentry-source-file="index.tsx">
                {t('bookingPage.book_consult')}
              </Button>
            </DialogFooter>
          </div>

          <div className={classNames('', {
          hidden: !openOTPModal
        })}>
            <DialogHeader data-sentry-element="DialogHeader" data-sentry-source-file="index.tsx">
              <DialogTitle data-sentry-element="DialogTitle" data-sentry-source-file="index.tsx">
                <Button variant="circle" size="icon" className="bg-bgSecondary" onClick={hideOTPModal} data-testid={featureBookingModalConfig.ids.bookingBackButton} data-sentry-element="Button" data-sentry-source-file="index.tsx">
                  <svg className="h-3.5 w-2.5 rotate-180 stroke-textPrimary stroke-1.5" data-sentry-element="svg" data-sentry-source-file="index.tsx">
                    <use xlinkHref={`${btnIcons}#arrow`} data-sentry-element="use" data-sentry-source-file="index.tsx"></use>
                  </svg>
                </Button>

                <Button variant="circle" size="icon" className="bg-bgSecondary" onClick={hideModal} data-testid={featureBookingModalConfig.ids.bookingCloseOTPButton} data-sentry-element="Button" data-sentry-source-file="index.tsx">
                  <svg className="size-6 rotate-180 stroke-textPrimary" data-sentry-element="svg" data-sentry-source-file="index.tsx">
                    <use xlinkHref={`${btnIcons}#close`} data-sentry-element="use" data-sentry-source-file="index.tsx"></use>
                  </svg>
                </Button>
              </DialogTitle>
            </DialogHeader>
            <div>
              <p className="mb-1 text-center text-xl font-bold md:text-2xl" data-testid={featureBookingModalConfig.ids.bookingOTPPhoneNumber}>
                {t('bookingPage.book_sms')} <br /> {makeBookingData.phoneNumber}
              </p>
              <p className="text-center text-base">{t('bookingPage.enter_sms')}</p>

              <BookingOTPForm visible={openOTPModal} otp={otp} statusOTP={statusOTP} statusOTPMessage={statusOTPMessage} changeOTP={(newValue: string) => changeOTP(newValue, successOTPHandler)} data-sentry-element="BookingOTPForm" data-sentry-source-file="index.tsx" />
            </div>
            <DialogFooter data-sentry-element="DialogFooter" data-sentry-source-file="index.tsx">
              <Button size="lg" className="mt-4 w-full" variant={timerNewCode ? 'secondary' : 'default'} disabled={Boolean(timerNewCode)} onClick={submitNewCodeHandler} data-testid={featureBookingModalConfig.ids.bookingRepeatSMSButton} data-sentry-element="Button" data-sentry-source-file="index.tsx">
                {t('bookingPage.repeat_sms')}
                {` ${timerNewCode}`}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
      {showSuccessToast && <TrackEvent eventName="success_simple_booking" formData={`тип записи: ${makeBookingData.bookingType}, желаемое время: ${makeBookingData.preferredTime}, номер: ${makeBookingData.phoneNumber}, ИИН: ${makeBookingData.iin}`} doctorName={doctorName} medcenterName={medcenterName} pageType={pageType} />}
    </>;
};